import { useTranslation } from 'react-i18next';
import { ChangeEvent } from 'react';
import { Button } from 'react-bootstrap';

type PeriodField = 'periodStartDate' | 'periodEndDate' | 'periodInstant';

interface PeriodValues {
  periodStartDate?: string | null;
  periodEndDate?: string | null;
  periodInstant?: string | null;
}

interface DateInputProps {
  contextId: string;
  field: PeriodField;
  value?: string | null;
  onChange: (contextId: string, field: PeriodField, value: string | null) => void;
}

const DateInput = ({ contextId, field, value, onChange }: DateInputProps) => (
  <input
    type="text"
    className="roll-forward__date-input"
    placeholder="YYYY-MM-DD"
    value={value ?? ''}
    onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(contextId, field, e.target.value || null)}
  />
);

interface PreviewContext {
  contextId: string;
  label?: string;
  periodForever?: boolean;
  periodStartDate?: string | null;
  periodEndDate?: string | null;
  periodInstant?: string | null;
}

interface StepPeriodsProps {
  contexts: PreviewContext[];
  periods: Record<string, PeriodValues>;
  onChange: (contextId: string, field: PeriodField, value: string | null) => void;
  onShiftAll: () => void;
}

const StepPeriods = ({ contexts, periods, onChange, onShiftAll }: StepPeriodsProps) => {
  const { t } = useTranslation('roll-forward');

  return (
    <div className="roll-forward__step">
      <div className="roll-forward__periods-head">
        <h4 className="roll-forward__heading">{t('step.periods')}</h4>
        <Button variant="outline-primary" size="sm" onClick={onShiftAll}>
          {t('shift-all')}
        </Button>
      </div>
      {contexts.length === 0 ? <p className="text-gray-500">{t('no-editable-periods')}</p> : null}
      <ul className="roll-forward__context-list">
        {contexts.map((c) => {
          const p = periods[c.contextId] ?? {};
          const isInstant = !!c.periodInstant;
          return (
            <li key={c.contextId} className="roll-forward__context-row">
              <span className="roll-forward__context-label">{c.label}</span>
              <span className="roll-forward__context-inputs">
                {isInstant ? (
                  <DateInput contextId={c.contextId} field="periodInstant" value={p.periodInstant} onChange={onChange} />
                ) : (
                  <>
                    <DateInput contextId={c.contextId} field="periodStartDate" value={p.periodStartDate} onChange={onChange} />
                    <span className="roll-forward__date-sep">→</span>
                    <DateInput contextId={c.contextId} field="periodEndDate" value={p.periodEndDate} onChange={onChange} />
                  </>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default StepPeriods;
